import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FolderPlus, CornerDownRight } from 'lucide-react';

/**
 * CreateSectionModal
 * ------------------
 * "New section after this slide…" — naming a structural section before the
 * deck changes.
 *
 * This is not the Section Header slide preset. A section here is structural:
 * it owns slides, it appears in the outline, it is reordered and exported as a
 * unit, and its title is what the slide header falls back to. Creating one
 * splits the selected slide's section in two.
 *
 * **Why a dialog rather than an inline prompt.** The operation moves slides,
 * and which slides it moves is not obvious from the menu item that opened it:
 * "after this slide" means everything below it *in this section*, which stops
 * at the next section boundary rather than at the end of the deck. Saying so
 * before anything moves is cheaper than an undo, and the preview is the main
 * content of the dialog for that reason.
 *
 * **The list is not a selection UI.** There are no checkboxes on purpose.
 * Section membership is contiguous — a section that owned slides 9 and 11 but
 * not 10 is not something the outline, the exporters or the reordering tools
 * can represent — so the set of slides that move is determined entirely by
 * which slide was pointed at. Anyone wanting a different arrangement has
 * "Move to section…" and drag reordering, which are built for it.
 *
 * **The palette is not free choice.** Light mode is a `.light-mode` class on an
 * ancestor with a sheet of `!important` overrides keyed to specific Tailwind
 * utilities (LessonTemplate.css) — `bg-gray-900`, `bg-gray-800`,
 * `border-gray-700`, `text-gray-100…500`, `text-blue-400`, `bg-blue-900/20`.
 * Anything outside that set keeps its dark value on a light panel: `text-white`
 * on a `bg-gray-900` heading is white-on-white the moment the theme flips, and
 * a `/40` tint stays a dark wash over white. So every colour here is one the
 * sheet already knows how to repaint, and a new one must be added to that sheet
 * rather than used raw.
 */

/** The one thing that makes the form invalid. */
export const isValidSectionTitle = (value) => Boolean(String(value ?? '').trim());

const CreateSectionModal = ({
  isOpen,
  /** `{ deckNumber, name }` for the slide the new section starts after. */
  afterSlide,
  /** The section being split, for the sentence that names it. */
  fromSectionTitle = '',
  /** `[{ slideId, deckNumber, name }]` — the slides that will move, in order. */
  trailingSlides = [],
  onCreate,
  onClose
}) => {
  const [title, setTitle] = useState('');
  const inputRef = useRef(null);
  // Where focus came from, so closing hands it back rather than dropping it on
  // <body> — the menu item that opened this has usually gone by then.
  const openerRef = useRef(null);

  const trimmed = title.trim();
  const canCreate = isValidSectionTitle(title);
  const movingCount = trailingSlides.length;

  useEffect(() => {
    if (!isOpen) return;
    openerRef.current = document.activeElement;
    // A fresh name every time. Carrying the last one over means a second
    // section quietly created with the first one's title.
    setTitle('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // Stopped here so the deck's own Escape handlers — which clear slide
      // picks and leave full screen — do not also fire behind the dialog.
      event.stopPropagation();
      event.preventDefault();
      onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    // The name field: it is the only thing the dialog needs, and typing is the
    // first thing anyone does here.
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [isOpen]);

  const close = () => {
    const opener = openerRef.current;
    onClose?.();
    if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
      opener.focus();
    }
  };

  const submit = (event) => {
    event?.preventDefault?.();
    if (!canCreate) return;
    onCreate?.(trimmed);
  };

  const previewName = useMemo(
    () => trimmed || 'New section',
    [trimmed]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 p-4"
      // data-app-modal is what the deck's undo, the block shortcuts, the slide
      // clipboard and Delete-on-a-row all look for before acting: a shortcut
      // that fires behind this would change something the author cannot see.
      data-app-modal="create-section"
      onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-section-title"
        onSubmit={submit}
        // max-h + flex column: the slide list scrolls inside the dialog rather
        // than growing the page, so the footer stays reachable on a laptop
        // screen however many slides are moving.
        className="flex max-h-[min(88vh,44rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl"
      >
        <div className="flex shrink-0 items-start gap-3 border-b border-gray-700 px-5 py-4">
          <FolderPlus size={20} className="mt-0.5 shrink-0 text-blue-400" aria-hidden="true" />
          <div className="min-w-0">
            <h2 id="create-section-title" className="text-base font-bold text-gray-100">
              Create New Section
            </h2>
            <p className="mt-1 text-xs text-gray-400">
              {movingCount > 0
                ? 'The slides below move into it. The slide you chose stays where it is.'
                : 'It starts with one blank slide, ready for new content.'}
            </p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <label htmlFor="create-section-name" className="block text-xs font-semibold text-gray-300">
            Section name
          </label>
          <input
            id="create-section-name"
            ref={inputRef}
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Animation Principles"
            autoComplete="off"
            className="mt-1.5 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          <div className="mt-4 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              New section will begin after
            </p>
            <p className="mt-1 break-words text-sm font-semibold text-gray-100">
              {afterSlide?.deckNumber ? `Slide ${afterSlide.deckNumber} — ` : ''}
              {afterSlide?.name || 'the selected slide'}
            </p>
            {fromSectionTitle && (
              <p className="mt-0.5 text-xs text-gray-400">
                Currently in <span className="font-semibold text-gray-300">{fromSectionTitle}</span>
              </p>
            )}
          </div>

          {movingCount > 0 ? (
            <div className="mt-4">
              <p className="text-xs text-gray-300">
                <span className="font-semibold text-gray-100">
                  {movingCount === 1 ? '1 slide' : `${movingCount} slides`}
                </span>
                {movingCount === 1 ? ' currently follows' : ' currently follow'} this slide.
                {movingCount === 1 ? ' It' : ' They'} will become part of the new section.
              </p>

              {/* The result, drawn. The rule between the two groups is what the
                  dialog is really saying, and a reader should be able to take
                  it in without reading the sentence above. It carries a text
                  label rather than relying on the line alone. */}
              <ol className="mt-2 space-y-1 rounded-lg border border-gray-700 bg-gray-800 p-2">
                <li className="flex items-baseline gap-2 rounded px-2 py-1 text-xs text-gray-400">
                  <span className="w-8 shrink-0 tabular-nums text-right">
                    {afterSlide?.deckNumber || ''}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{afterSlide?.name || 'Selected slide'}</span>
                  <span className="shrink-0 text-[10px] font-semibold uppercase text-gray-400">stays</span>
                </li>

                <li aria-hidden="true" className="flex items-center gap-2 px-2 py-1">
                  <span className="h-px flex-1 bg-blue-500/60" />
                  <span className="truncate text-[10px] font-bold uppercase tracking-wide text-blue-400">
                    New section: {previewName}
                  </span>
                  <span className="h-px flex-1 bg-blue-500/60" />
                </li>

                {trailingSlides.map((slide) => (
                  <li
                    key={slide.slideId}
                    className="flex items-baseline gap-2 rounded bg-blue-900/20 px-2 py-1 text-xs text-gray-200"
                  >
                    <span className="w-8 shrink-0 tabular-nums text-right text-gray-400">
                      {slide.deckNumber || ''}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{slide.name}</span>
                    <CornerDownRight size={12} className="shrink-0 text-blue-400" aria-hidden="true" />
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <p className="mt-4 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-xs text-gray-300">
              This is the last slide in {fromSectionTitle ? `"${fromSectionTitle}"` : 'its section'}, so
              there is nothing to move. The new section is created straight after it with one blank
              slide. Any section that already follows is left exactly as it is.
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-700 px-5 py-3">
          <button
            type="button"
            onClick={close}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canCreate}
            aria-label={movingCount > 0
              ? `Create section with ${movingCount} slide${movingCount === 1 ? '' : 's'}`
              : 'Create section with a blank slide'}
            className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Create Section
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateSectionModal;
